import { inject, Injectable } from '@angular/core';
import {
  isNotNull,
  type OfType,
  type SelectableOption,
  type SelectableOptionGroup,
} from '@reconcept-platform/shared/data-access';
import {
  type UpdateWorkspaceInput,
  type WorkspaceFilter,
  type WorkspaceWithActiveCardsQuery,
} from '@reconcept-platform/shared/types';
import { type SelectedFile, sortByKey } from '@reconcept-platform/shared/util';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  WorkspaceApi,
  type WorkspaceDetailQuery_Workspace,
  type WorkspaceDomainAndSubDomainsQueryQuery_Subdomain,
} from '../../api/workspace/workspace.api';
import { CardTaskStatusEnum } from '../../schema/schema-enums';
import { type ConnectionNode } from '../../utils/connection.types';
import { UserService, type UserView } from '../user/user.service';

export type WorkspaceWithActiveCardsQuery_Workspace = OfType<
  NonNullable<WorkspaceWithActiveCardsQuery['node']>,
  'Workspace'
>;
export type WorkspaceWithActiveCardsQuery_EventCard_Task = ConnectionNode<
  ConnectionNode<WorkspaceWithActiveCardsQuery_Workspace['eventCards']>['cardTasks']
>;
export type WorkspaceWithActiveCardsQuery_GoalCard_Task = ConnectionNode<
  ConnectionNode<WorkspaceWithActiveCardsQuery_Workspace['eventCards']>['cardTasks']
>;

@Injectable({
  providedIn: 'root',
})
export class WorkspaceService {
  private workspaceApi = inject(WorkspaceApi);
  private userService = inject(UserService);

  updateFiles(workspaceId: string, files: SelectedFile[]) {
    return this.workspaceApi.updateWorkspace({
      workspaceId: workspaceId,
      attachFiles: files.map((file) => file.directUploadId),
    });
  }

  watchAllWorkspaces() {
    return this.workspaceApi.watchAllWorkspaces();
  }

  watchWorkspace(workspaceId: string): Observable<WorkspaceDetailQuery_Workspace | null> {
    return this.workspaceApi.watchWorkspace(workspaceId);
  }

  watchWorkspaceWithRoles(workspaceId: string) {
    return this.workspaceApi.watchWorkspaceWithRoles(workspaceId);
  }

  watchWorkspaceScopeForRoleModal(workspaceId: string) {
    return this.workspaceApi.watchWorkspaceScopeForRoleModal(workspaceId);
  }

  watchSelectableWorkspaceRoleUsers(workspaceId: string): Observable<SelectableOption<UserView>[]> {
    return this.watchWorkspaceWithRoles(workspaceId).pipe(
      map((workspace) => {
        return (workspace?.workspaceRoles.edges ?? [])
          .map((role) => role.node)
          .filter(isNotNull)
          .map((role) => this.userService.mapToUserView(role.user));
      }),
      map((users) => {
        const selectableUsers = users.map((user) => {
          return {
            value: user,
            label: user.fullName,
            avatar: true,
          };
        });

        return sortByKey(selectableUsers, 'value.lastName');
      }),
    );
  }

  watchSelectableWorkspaceSubDomainsGroups(
    workspaceId: string,
  ): Observable<SelectableOptionGroup<WorkspaceDomainAndSubDomainsQueryQuery_Subdomain>[]> {
    return this.workspaceApi.watchWorkspaceDomainAndSubDomains(workspaceId).pipe(
      map((workspace) => {
        return (workspace?.dashboard?.dashboardFormat?.parentDomains?.nodes ?? []).map((parentDomainEdge) => {
          return {
            label: parentDomainEdge.title,
            children: (parentDomainEdge.subDomains.nodes ?? []).map((subDomainEdge) => {
              return {
                value: subDomainEdge,
                label: subDomainEdge.title,
              };
            }),
          };
        });
      }),
    );
  }

  watchWorkspaceDetailMenuItems(workspaceId: string, workspaceFilter: WorkspaceFilter) {
    return this.workspaceApi.watchWorkspaceDetailMenuItems(workspaceId, workspaceFilter);
  }

  watchWorkspaceDomains(workspaceId: string) {
    return this.workspaceApi.watchWorkspaceDomains(workspaceId);
  }

  updateWorkspace(updateWorkspaceInput: UpdateWorkspaceInput) {
    return this.workspaceApi.updateWorkspace(updateWorkspaceInput);
  }

  watchWorkspaceWithActiveCards(workspaceId: string) {
    return this.workspaceApi.watchWorkspaceWithCards(workspaceId).pipe(
      map((workspaceView) => {
        if (!workspaceView) {
          return null;
        }

        return {
          ...workspaceView,
          goalCards: workspaceView.goalCards?.nodes?.map((goalCard) => ({
            ...goalCard,
            cardTasks: { ...goalCard.cardTasks, nodes: this.onlyOpenCardTasks(goalCard.cardTasks.nodes) },
          })),
          eventCards: workspaceView.eventCards?.nodes?.map((eventCard) => ({
            ...eventCard,
            cardTasks: { ...eventCard.cardTasks, nodes: this.onlyOpenCardTasks(eventCard.cardTasks.nodes) },
          })),
        };
      }),
      map((workspaceView) => {
        if (!workspaceView) {
          return null;
        }

        return {
          ...workspaceView,
          goalCards: workspaceView.goalCards?.filter((goalCard) => goalCard.cardTasks.nodes.length),
          eventCards: workspaceView.eventCards?.filter((eventCard) => eventCard.cardTasks.nodes.length),
        };
      }),
    );
  }

  private onlyOpenCardTasks(
    cardTasks:
      | (WorkspaceWithActiveCardsQuery_EventCard_Task | WorkspaceWithActiveCardsQuery_GoalCard_Task | null)[]
      | null
      | undefined,
  ) {
    return (cardTasks ?? []).filter(isNotNull).filter((it) => it.cardTaskStatus === CardTaskStatusEnum.OPEN);
  }
}
